import { NotificationI } from "@/lib/types/notification";
import { NotificationCampaignImage } from "./campaign";
import { NotificationIndividualImage } from "./individual";
import { useMemo } from "react";
import { NotificationTypeIcon } from "./type-icon";
import { NOTIFICATION_TYPES } from "shared/lib/constants/notifications";

export type NotificationImageI = NotificationI;

const isAvailabilityAnswer = (type?: string) =>
  type === NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_APPROVED ||
  type === NOTIFICATION_TYPES.DIAL_BLOCK_AVAILABILITY_DENIED;

// A strike alert (C12-D200) is about a caller, not a campaign.
const isStrikeAlert = (type?: string) =>
  type === NOTIFICATION_TYPES.DIAL_BLOCK_STRIKE_ALERT;

// So is a request-waiting alert (C-12 wave 2 B, C12-D235).
const isRequestWaiting = (type?: string) =>
  type === NOTIFICATION_TYPES.DIAL_BLOCK_REQUEST_WAITING;

// And an incentive bell (C-16 S1) is about a contest.
const isIncentive = (type?: string) =>
  type === NOTIFICATION_TYPES.INCENTIVE_STARTED ||
  type === NOTIFICATION_TYPES.INCENTIVE_WON;

// Top 10 bells (C-16 S4): the due card shows its lead's campaign, the
// morning digest is about the caller's Top 10 as a whole.
const isTopTenDue = (type?: string) => type === NOTIFICATION_TYPES.TOP_TEN_DUE;
const isTopTenDigest = (type?: string) =>
  type === NOTIFICATION_TYPES.TOP_TEN_DIGEST;

export const NotificationImage = (props: NotificationImageI) => {
  const {
    notification_type,
    contact_name,
    campaign_id,
    campaign_name,
    dial_block_strike_alert,
    dial_block_request_waiting,
    top_ten_due,
  } = props;
  const hasNoCampaign =
    isAvailabilityAnswer(notification_type) ||
    isStrikeAlert(notification_type) ||
    isRequestWaiting(notification_type) ||
    isIncentive(notification_type) ||
    isTopTenDigest(notification_type);

  const isIndividual = useMemo(() => {
    return [
      NOTIFICATION_TYPES.MISSED_CALL as string,
      NOTIFICATION_TYPES.EMAIL_OPENED,
      NOTIFICATION_TYPES.EMAIL_RECEIVED,
      NOTIFICATION_TYPES.COLD_OUTREACH_EMAIL_RESPONDED,
      NOTIFICATION_TYPES.CALENDAR_INVITE_RECEIVED,
    ].includes(notification_type as string);
  }, [notification_type]);

  return (
    <div className="relative">
      {isIndividual ? (
        <NotificationIndividualImage contact_name={contact_name} />
      ) : (
        <NotificationCampaignImage
          // Availability answers carry the zero UUID and strike alerts no
          // campaign: nothing to look up.
          campaign_id={
            hasNoCampaign
              ? undefined
              : campaign_id ||
                (isTopTenDue(notification_type)
                  ? top_ten_due?.campaign_id
                  : undefined)
          }
          // Their initials say My Week, or the alert's workspace.
          campaign_name={
            campaign_name ||
            (isAvailabilityAnswer(notification_type)
              ? "My Week"
              : isStrikeAlert(notification_type)
                ? dial_block_strike_alert?.workspace_name || "Rep Health"
                : isRequestWaiting(notification_type)
                  ? dial_block_request_waiting?.workspace_name || "Requests"
                  : isIncentive(notification_type)
                    ? "Rewards"
                    : isTopTenDue(notification_type)
                      ? top_ten_due?.campaign_name
                      : isTopTenDigest(notification_type)
                        ? "Top 10"
                        : undefined)
          }
        />
      )}

      <NotificationTypeIcon {...props} />
    </div>
  );
};
